import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronRight, ChevronLeft, Lightbulb, Check } from 'lucide-react';

// Onboarding steps based on user role
const getOnboardingSteps = (role) => {
  const commonSteps = [
    {
      id: 'welcome',
      title: 'Welcome to Jax Portal',
      description: 'This quick tour will help you get started with the key features.',
      target: null,
      position: 'center'
    },
    {
      id: 'dashboard',
      title: 'Your Dashboard',
      description: 'Get an overview of active jobs, team performance, and key metrics at a glance.',
      target: '[data-testid="nav-dashboard"]',
      position: 'right'
    },
    {
      id: 'jobs',
      title: 'Jobs Pipeline',
      description: 'Track all your projects from bid to completion. Create new jobs, update status, and manage timelines.',
      target: '[data-testid="nav-jobs"]',
      position: 'right'
    },
    {
      id: 'floor-plans',
      title: 'Floor Plans & 3D Visualization',
      description: 'Upload floor plans, add electrical/plumbing layers, and generate photorealistic 3D renders with AI.',
      target: '[data-testid="nav-floor-plans"]',
      position: 'right'
    },
    {
      id: 'calendar',
      title: 'Calendar & Scheduling',
      description: 'Schedule jobs, maintenance, and meetings. Sync with Google Calendar for easy access.',
      target: '[data-testid="nav-calendar"]',
      position: 'right'
    }
  ];

  const cooSteps = [
    ...commonSteps,
    {
      id: 'accounting',
      title: 'Accounting Suite',
      description: 'Full financial management - invoices, expenses, profit tracking, and cash flow analysis.',
      target: '[data-testid="nav-accounting"]',
      position: 'right'
    },
    {
      id: 'business-plan',
      title: 'Business Planning',
      description: 'Set goals, track KPIs, and forecast growth with our planning tools.',
      target: '[data-testid="nav-business-plan"]',
      position: 'right'
    },
    {
      id: 'legal',
      title: 'Legal & Proprietary',
      description: 'Review important legal notices and proprietary system agreements.',
      target: '[data-testid="nav-legal"]',
      position: 'right'
    }
  ];

  const presidentSteps = [
    ...commonSteps,
    {
      id: 'team',
      title: 'Team Builder',
      description: 'View and manage your crews. See who\'s assigned to each project.',
      target: '[data-testid="nav-team"]',
      position: 'right'
    },
    {
      id: 'inventory',
      title: 'Inventory Management',
      description: 'Track equipment, materials, and supplies across all your projects.',
      target: '[data-testid="nav-inventory"]',
      position: 'right'
    }
  ];

  const clientSteps = [
    {
      id: 'welcome',
      title: 'Welcome to Your Project Portal',
      description: 'Track your remodel progress and stay connected with our team.',
      target: null,
      position: 'center'
    },
    {
      id: 'progress',
      title: 'Project Progress',
      description: 'See your project\'s current status, completion percentage, and upcoming milestones.',
      target: '[data-testid="project-progress"]',
      position: 'bottom'
    },
    {
      id: 'phases',
      title: 'Project Phases',
      description: 'Review each phase of your project and see which tasks are complete.',
      target: '[data-testid="project-phases"]',
      position: 'bottom'
    },
    {
      id: 'documents',
      title: 'Shared Documents',
      description: 'Access contracts, change orders, and other important documents.',
      target: '[data-testid="shared-documents"]',
      position: 'left'
    },
    {
      id: 'contact',
      title: 'Contact Us',
      description: 'Have questions? Reach out to our team anytime.',
      target: '[data-testid="contact-info"]',
      position: 'left'
    }
  ];

  switch (role) {
    case 'coo':
      return cooSteps;
    case 'president':
      return presidentSteps;
    case 'client':
      return clientSteps;
    default:
      return commonSteps;
  }
};

export const OnboardingTour = ({ role = 'coo', onComplete }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const steps = getOnboardingSteps(role);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    // Save to localStorage that onboarding is complete
    localStorage.setItem(`onboarding_complete_${role}`, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem(`onboarding_complete_${role}`, 'true');
    setIsVisible(false);
    if (onComplete) onComplete();
  };

  if (!isVisible) return null;

  const step = steps[currentStep];
  const isCenter = step.position === 'center' || !step.target;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] bg-black/60"
        data-testid="onboarding-overlay"
      >
        {/* Spotlight effect for targeted elements */}
        {step.target && (
          <style>{`
            ${step.target} {
              position: relative;
              z-index: 101;
              box-shadow: 0 0 0 4px #C6A87C, 0 0 20px rgba(198, 168, 124, 0.5);
              border-radius: 4px;
            }
          `}</style>
        )}

        {/* Tooltip/Modal */}
        <motion.div
          key={step.id}
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: -20 }}
          className={`absolute bg-white shadow-2xl max-w-md ${
            isCenter 
              ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2' 
              : 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 md:top-auto md:left-auto md:translate-x-0 md:translate-y-0 md:top-24 md:left-72'
          }`}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#0A0A0A] to-[#1a1a1a] p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-[#C6A87C] rounded-full flex items-center justify-center">
                <Lightbulb className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-[#C6A87C] text-xs font-semibold uppercase tracking-wider">
                  Step {currentStep + 1} of {steps.length}
                </p>
                <h3 className="text-white font-heading text-lg">{step.title}</h3>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="text-gray-400 hover:text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <p className="text-gray-600 leading-relaxed">{step.description}</p>
          </div>

          {/* Progress dots */}
          <div className="px-6 pb-4 flex justify-center gap-2">
            {steps.map((_, idx) => (
              <div
                key={idx}
                className={`w-2 h-2 rounded-full transition-colors ${
                  idx === currentStep ? 'bg-[#C6A87C]' : idx < currentStep ? 'bg-green-500' : 'bg-gray-200'
                }`}
              />
            ))}
          </div>

          {/* Actions */}
          <div className="p-4 bg-gray-50 flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              Skip Tour
            </button>
            <div className="flex items-center gap-3">
              {currentStep > 0 && (
                <button
                  onClick={handlePrev}
                  className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-800 transition-colors"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Back
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex items-center gap-1 px-6 py-2 bg-[#C6A87C] text-white text-sm font-semibold hover:bg-[#B09265] transition-colors"
                data-testid="onboarding-next-btn"
              >
                {currentStep === steps.length - 1 ? (
                  <>
                    <Check className="w-4 h-4" />
                    Get Started
                  </>
                ) : (
                  <>
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// Hook to check if onboarding should show
export const useOnboarding = (role) => {
  const [shouldShow, setShouldShow] = useState(false);

  useEffect(() => {
    const isComplete = localStorage.getItem(`onboarding_complete_${role}`);
    if (!isComplete) {
      // Delay showing onboarding to let the page load
      const timer = setTimeout(() => setShouldShow(true), 1000);
      return () => clearTimeout(timer);
    }
  }, [role]);

  const resetOnboarding = () => {
    localStorage.removeItem(`onboarding_complete_${role}`);
    setShouldShow(true);
  };

  return { shouldShow, setShouldShow, resetOnboarding };
};
